/**
 * Utilities for interpreting API errors, as defined in our API codebase
 * at https://github.com/butlrtechnologies/butlr-api-container/blob/main/pkg/common/butlrerror/error.go
 * (as of writing)
 */

/**
 * Structural shape that covers both `GraphQLError` (thrown by Apollo's
 * link layer) and `GraphQLFormattedError` (returned in `MutationResult.errors`
 * under errorPolicy: 'all'). Both have `message` and `extensions` with the
 * same value shape; we don't read any GraphQLError-only fields here, so
 * accepting the structural shape lets callers drop `as GraphQLError` casts.
 */
export type GraphQLErrorLike = {
  readonly message: string;
  readonly extensions?: { [key: string]: unknown } | null | undefined;
};

/**
 * Specific error codes that the frontend keys behavior off (not just the
 * group prefix). Add one here when a code needs more than the group-level
 * helpers (`isErrorNotFound` etc.) — so the frontend has exactly one
 * place to update if the backend ever renumbers. Mirrors
 * butlr-api-container/pkg/common/butlrerror/codes.go.
 */
export const ERROR_USER_NOT_FOUND = 40500;

/**
 * True iff the GraphQL error is the auth-API's "no idps_users row for
 * this JWT subject" signal. Used by SpaceAccessContext's circuit breaker
 * to recognize legacy users who pre-date User Management and grant them
 * unrestricted access until a real grant is created.
 */
export function isErrorUserNotFound(error: GraphQLErrorLike): boolean {
  return error.extensions?.code === ERROR_USER_NOT_FOUND;
}

/**
 * Our error codes are 5-digit numbers which begin
 * with an HTTP code prefix. This function extracts
 * the HTTP code from the error code.
 */
export function getErrorCodeGroup(error: GraphQLErrorLike): number | null {
  const code = error.extensions?.code;
  if (!code || typeof code !== 'number') {
    return null;
  }
  return Math.floor(code / 100);
}

export function isErrorNotAuthenticated(error: GraphQLErrorLike) {
  return getErrorCodeGroup(error) === 401;
}

export function isErrorBadRequest(error: GraphQLErrorLike) {
  return getErrorCodeGroup(error) === 400;
}

export function isErrorNotFound(error: GraphQLErrorLike) {
  return getErrorCodeGroup(error) === 404;
}

export function isErrorConflict(error: GraphQLErrorLike) {
  return getErrorCodeGroup(error) === 409;
}

export function isErrorInternal(error: GraphQLErrorLike) {
  return getErrorCodeGroup(error) === 500;
}
